import React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import { mediaQuery } from '@/utils/helpers';
import { config } from '@keytrade/functions';
import { Badge } from '@keytrade/components-badge';
import { DateTime } from 'luxon';
import slugify from 'slugify';

import useTranslations from '@/hooks/useTranslations';

const { list: colors } = config.colors;
const { getFont } = config.fonts;

const VacancyBlock = styled(Link)`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${colors.Grey};
  padding: 16px 0px 18px 0;
  text-decoration: none;
  &:last-of-type {
    border-bottom: none;
  }
  ${mediaQuery.md(`
    border-radius: 12px;
    border: 1px solid ${colors.BlueLight};
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.05), 0px 2px 7px 0px rgba(0, 0, 0, 0.05);
    width: 100%;
    max-width: 840px;
    padding: 16px 24px;
    margin: 12px 0;
    &:first-of-type
    {
      margin-top: 0;
    }
    &:hover {
      box-shadow: rgb(0 0 0 / 8%) 0px 15px 20px 0px, rgb(0 0 0 / 8%) 0px 19px 40px 0px;
    }
  `)}
`;

const VacancyInformation = styled.div`
  margin-bottom: 4px;
  ${mediaQuery.md(`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
  `)}
`;

const TypeInformation = styled.div`
  ${mediaQuery.md(`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
  `)}
`;

const VacancyTitle = styled.span`
  display: block;
  font-size: 16px;
  line-height: 24px;
  font-family: ${({ theme }) => getFont(theme, 'semiBold')};
  color: ${colors.BlueDawn};
  ${mediaQuery.md(`
    font-size: 18px;
    line-height: 28px;
    font-family: ${({ theme }) => getFont(theme, 'bold')};
    max-width: 70%;
  `)}
`;
const VacancyType = styled.span`
  display: block;
  text-align: left;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 14px;
  color: ${colors.BlueDark};
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  ${mediaQuery.md(`
    font-size: 18px;
    line-height: 28px;
    margin-bottom: 0;
  `)}
`;
const Timestamp = styled.span`
  display: none;
  font-size: 14px;
  line-height: 20px;
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  text-align: right;
  color: ${colors.BlueMoon};
  ${mediaQuery.md(`
    display: block;
  `)}
`;
const ContractType = styled.span``;
const LevelCategory = styled.span`
  margin-left: 10px;
`;

export type VacancyType = {
  contractType: string;
  category: string;
  beginPublication: string;
  code: string;
  languageId: string;
  title: string;
  type: string;
  isSpontaneous: boolean;
  locale: string;
};

type Props = VacancyType;
const Vacancy: React.FC<Props> = ({
  title,
  type,
  contractType,
  category,
  beginPublication,
  code,
  locale,
}) => {
  const { t } = useTranslations('jobs');

  const calculateTimestamp = () => {
    const date = DateTime.fromISO(beginPublication);
    const differ = DateTime.local().diff(date, [
      'years',
      'months',
      'days',
      'hours',
    ]);

    const publication = differ.toObject();
    if (
      publication.days > 0 &&
      publication.days <= 30 &&
      publication.months === 0
    ) {
      return `${t('timestamp_lable_prefix_day')} ${publication.days}  ${t(
        'timestamp_lable_sufix_day',
      )}`;
    } else {
      return `${t('timestamp_lable_prefix_month')} ${publication.months}  ${t(
        'timestamp_lable_sufix_month',
      )}`;
    }
  };

  return (
    <VacancyBlock to={`/${locale}/job/${code}`}>
      <VacancyInformation>
        <VacancyTitle>{title}</VacancyTitle>
        <Timestamp>{calculateTimestamp()}</Timestamp>
      </VacancyInformation>
      <TypeInformation>
        <VacancyType>{type && t(`teams.${type}`)}</VacancyType>
        <div>
          {contractType && (
            <ContractType>
              <Badge kind='light' size='xs'>
                {t(`contracts.${contractType}`)}
              </Badge>
            </ContractType>
          )}
          {category && (
            <LevelCategory>
              <Badge kind='light' size='xs'>
                {t(`levels.${category}`)}
              </Badge>
            </LevelCategory>
          )}
        </div>
      </TypeInformation>
    </VacancyBlock>
  );
};

export default Vacancy;
